import { motion } from "framer-motion";
import { Wallet, Handshake, Palette, Lightbulb, LayoutGrid, Globe2, Headset, Music4, Truck, MessagesSquare } from "lucide-react";
import { Reveal } from "./Reveal";

const advantages = [
  { icon: Wallet, title: "Quality-to-Affordability", desc: "Premium standards paired with highly transparent, honest pricing." },
  { icon: Handshake, title: "End-to-End Vendor Coordination", desc: "We handle all outer contracts, scheduling and payments on your behalf." },
  { icon: Palette, title: "Custom Stage Decor", desc: "Tailored backdrops, entrance tunnels and exquisite floral work." },
  { icon: Lightbulb, title: "Lighting Engineering", desc: "Professional ambient, LED glow and function lighting setups." },
  { icon: LayoutGrid, title: "Diverse Service Portfolio", desc: "Birthdays, devotional gatherings and large corporate expos alike." },
  { icon: Globe2, title: "Pan-India Network", desc: "Extensive vendor networks across Central India and beyond." },
  { icon: Headset, title: "On-Site Coordination", desc: "Dedicated real-time staff to handle every hiccup seamlessly." },
  { icon: Music4, title: "Entertainment Management", desc: "Live bands, local dance groups and celebrity artists arranged." },
  { icon: Truck, title: "Transport & Logistics", desc: "Safe guest movement, parking flow and material delivery." },
  { icon: MessagesSquare, title: "Responsive Communication", desc: "Friendly, flexible and instant planning feedback throughout." },
];

export default function WhyChooseUs() {
  return (
    <section id="why-us" data-testid="why-us-section" className="bg-heirloom-bone py-24 md:py-28">
      <div className="max-w-[1300px] mx-auto px-6">
        <Reveal className="text-center mb-14">
          <span className="gold-rule overline text-heirloom-maroon">Why Choose Us</span>
          <h2 className="font-display text-4xl md:text-5xl text-heirloom-obsidian mt-4">
            10 Reasons to <span className="text-heirloom-maroon italic">Trust Arihant</span>
          </h2>
          <p className="text-heirloom-obsidian/60 max-w-2xl mx-auto mt-4">
            From flawless logistics to breathtaking decor — here&rsquo;s what makes us Central India&rsquo;s
            preferred celebration partner.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5">
          {advantages.map((a, i) => (
            <motion.div
              key={a.title}
              data-testid={`advantage-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
              className="group flex items-start gap-5 bg-heirloom-cream/60 border border-heirloom-line rounded-2xl p-6 hover:border-heirloom-maroon/40 hover:shadow-[0_18px_45px_-28px_rgba(0,0,0,0.4)] transition-all duration-400"
            >
              <div className="relative shrink-0">
                <span className="h-14 w-14 rounded-xl bg-heirloom-maroon/10 text-heirloom-maroon flex items-center justify-center group-hover:bg-heirloom-maroon group-hover:text-heirloom-bone transition-colors duration-400">
                  <a.icon size={24} />
                </span>
                <span className="absolute -top-2 -left-2 h-6 w-6 rounded-full bg-heirloom-gold text-heirloom-maroon-deep text-xs font-bold flex items-center justify-center">{i + 1}</span>
              </div>
              <div>
                <h3 className="font-display text-xl text-heirloom-obsidian">{a.title}</h3>
                <p className="text-sm text-heirloom-obsidian/60 mt-1.5 leading-relaxed">{a.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
